import type { UiSchema } from '@rjsf/utils';
import type { UAEntityTableComponentType } from '@unifyapps/defs/blocks/Table/UAEntityTable/types';
import getUaOptions from '@unifyapps/form/utils/getUaOptions';
import { cond } from '@unifyapps/hooks/useCond';
import type { TFunction } from '@unifyapps/i18n/client';
import { useTranslation } from '@unifyapps/i18n/client';
import type { Violation } from '@unifyapps/network/generated/models/violation';
import type { QueryKey } from '@unifyapps/network/react-query';
import { useQueryClient } from '@unifyapps/network/react-query';
import { UAError } from '@unifyapps/network/utils/consts/error';
import type { ErrorType } from '@unifyapps/network/utils/fetch';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import type { SnackbarProps } from '@unifyapps/ui/components/Snackbar/types';
import { useCallback } from 'react';
import { getAggregationQueryKeyToInvalidate } from './useAggregationQueryBuilder';

export function getResolvedErrorMessage(
  t: TFunction,
  error: ErrorType<unknown> | null | undefined,
) {
  if (!error) return t('common:ErrorState.SomethingWentWrong');
  const { payload } = error;
  const violationPayload = payload as { violations?: Violation[] };
  return cond(
    [
      payload && 'violations' in payload && Boolean(violationPayload.violations?.[0]?.message),
      violationPayload.violations?.[0]?.message,
    ],
    // guard on a non-empty trimmed value (not just key presence): the backend can send an empty
    // `message`/`errorMessage`, which would otherwise render as a blank title in the error banner /
    // snackbar instead of falling through to the generic fallback below.
    [Boolean(payload?.errorMessage?.trim()), payload?.errorMessage],
    [Boolean(payload?.message?.trim()), payload?.message],
    t('common:ErrorState.SomethingWentWrong'),
  );
}

const useMutationHelpers = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['common']);

  const onShowErrorSnackbar = useCallback(
    (snackbarProps?: Omit<SnackbarProps, 'color'>) => {
      showSnackbar({
        color: 'error',
        title: t('common:ErrorState.SomethingWentWrong'),
        ...snackbarProps,
      });
    },
    [showSnackbar, t],
  );

  const onShowSuccessSnackbar = useCallback(
    (snackbarProps: Omit<SnackbarProps, 'color'>) => {
      showSnackbar({ color: 'success', ...snackbarProps });
    },
    [showSnackbar],
  );

  const onInvalidateQueries = useCallback(
    (queryKeys: QueryKey[]) => {
      queryKeys.forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey });
      });
    },
    [queryClient],
  );

  const onRemoveQueries = useCallback(
    (queryKeys: QueryKey[]) => {
      queryKeys.forEach((queryKey) => {
        queryClient.removeQueries({ queryKey });
      });
    },
    [queryClient],
  );

  const onRefetchQueries = useCallback(
    async (queryKeys: QueryKey[]) => {
      await Promise.all(queryKeys.map((queryKey) => queryClient.refetchQueries({ queryKey })));
    },
    [queryClient],
  );

  const onInvalidateQueriesAsync = useCallback(
    async (queryKeys: QueryKey[]) => {
      await Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
    [queryClient],
  );

  const onMutationError = useCallback(
    (error?: ErrorType<unknown>) => {
      onShowErrorSnackbar({ title: getResolvedErrorMessage(t, error) });
    },
    [onShowErrorSnackbar, t],
  );

  const getInlineDuplicateError = useCallback(
    (uiSchema: UiSchema, error: ErrorType<unknown> | undefined, duplicateErrorMessage?: string) => {
      const uaOptions = getUaOptions(uiSchema);
      const uniqueKeyField = uaOptions.uniqueKeyField ?? ('' as string);

      if (error?.payload?.errorCode === UAError.DUPLICATE_KEY_ERROR) {
        const extraErrors = {
          [uniqueKeyField]: {
            __errors: [duplicateErrorMessage ?? getResolvedErrorMessage(t, error)],
          },
        };
        return extraErrors;
      }
    },
    [t],
  );

  const onInvalidateUAEntityBlock = useCallback(
    (component: UAEntityTableComponentType) => {
      void queryClient.invalidateQueries({
        queryKey: getAggregationQueryKeyToInvalidate(component),
        refetchType: 'all',
      });
    },
    [queryClient],
  );

  const onInvalidateUAEntityTableComponent = useCallback(
    (component: UAEntityTableComponentType) => {
      void queryClient.invalidateQueries({
        queryKey: getAggregationQueryKeyToInvalidate(component),
      });
    },
    [queryClient],
  );

  return {
    onMutationError,
    onShowSuccessSnackbar,
    onShowErrorSnackbar,
    onRemoveQueries,
    onInvalidateQueries,
    onInvalidateQueriesAsync,
    getInlineDuplicateError,
    onInvalidateUAEntityBlock,
    onInvalidateUAEntityTableComponent,
    onRefetchQueries,
  };
};

export default useMutationHelpers;
